/**
 * 「这一镜会画成」—— 把逐镜的结构化字段拼成一句人话(2026-08-06 用户审阅 Q3)。
 *
 * ═══ 治的是什么 ═══════════════════════════════════════════════════════════════
 * 用户原话:「目前我们只展示了环境和动作,但具体的画面内容是否更详细一些好,这样用户也可感知」。
 * 分镜卡上确实只有 环境(≤24 字)+ 动作(≤36 字)两个窄口能读 —— 契约 v2 之后导演不再产
 * prompt 文本,画面是编译层用**一堆结构化字段**拼出来的:景别 / 运镜 / 镜速 / 布光 / 承接 /
 * hero 标记 / 主体指代。那些字段在卡上是一排「景别 AI 定」的占位 chip,填了值也散在各处,
 * 没有一处把它们连成"这一镜长什么样"。
 *
 * ═══ ★为什么不直接显示引擎最终 prompt ═══════════════════════════════════════════
 * 那才是零失真的做法,但要改 ai(回传)+ api(透传落库)+ console 三仓契约。
 * 本模块是**零跨仓**的那一层:用的就是编译层真正的输入集合(同一批字段),
 * 所以它不会说出编译层看不到的东西 —— 但也**不等于**最终 prompt(编译层还会加风格、
 * 主体展开、负面词)。措辞因此写「会画成」而不是「提示词」,别让人以为这就是发出去的原文。
 *
 * ★空字段一律跳过,不写「AI 定」——「AI 定」是控件的占位话术,不是画面描述;
 *   把它拼进句子里等于告诉用户"这一镜会画成 AI 定、AI 定"。
 */
import {
  CAMERA_MOVE_LABEL,
  CAMERA_SPEED_LABEL,
  CONTINUITY_LABEL,
  LIGHTING_LABEL,
  SHOT_SIZE_LABEL,
} from "@/shared/lib/video/cameraLexicon";

export interface ShotSummaryInput {
  scene?: string | null;
  motion?: string | null;
  shotSize?: string | null;
  cameraMovement?: string | null;
  cameraSpeed?: string | null;
  lightingKey?: string | null;
  continuity?: string | null;
  heroMoment?: boolean | null;
}

/** 查表取人读标签;查不到就原样回(枚举扩了但前端词表没跟时,显示原值好过显示空)。 */
function lex(map: Record<string, string>, v?: string | null): string | null {
  const k = (v ?? "").trim();
  if (!k) return null;
  return map[k] ?? k;
}

/**
 * 拼成一句:`中景 · 缓慢推近 · 侧逆光 —— 厨房台面油污特写，手指划过油污`
 * 全空返 null(调用方据此不渲染整行,而不是渲一行空壳)。
 */
export function shotSummary(s: ShotSummaryInput): string | null {
  const lens = [
    lex(SHOT_SIZE_LABEL, s.shotSize),
    lex(CAMERA_MOVE_LABEL, s.cameraMovement),
    lex(CAMERA_SPEED_LABEL, s.cameraSpeed),
    lex(LIGHTING_LABEL, s.lightingKey),
  ].filter(Boolean);

  // 场景与动作用全角「，」接(1006 复测:半角「,」夹在全角中文里,与卡上其它标点混用)
  const content = [(s.scene ?? "").trim(), (s.motion ?? "").trim()].filter(Boolean).join("，");

  if (lens.length === 0 && !content) return null;

  const head = s.heroMoment ? ["★重点镜", ...lens] : lens;
  const parts: string[] = [];
  if (head.length > 0) parts.push(head.join(" · "));
  if (content) parts.push(content);
  // 承接放句尾:它说的是「跟上一镜的关系」,不是本镜画面本身
  const cont = lex(CONTINUITY_LABEL, s.continuity);
  const tail = cont ? `(${cont})` : "";
  return parts.join(" —— ") + tail;
}

/**
 * 镜头语言短摘要 —— 矮卡上那一行(`特写 · 缓推 · 柔光`)。
 *
 * 与 `shotSummary` 的分工:那个是"这一镜会画成什么"的整句(含环境/动作),给右栏检视用;
 * 这个只要**镜头语言**几个词,因为矮卡上环境/动作已经单独占一行了 —— 重复写两遍
 * 只会把 150px 的卡撑回去。空字段一律跳过,全空返 null(整行不渲染)。
 */
export function lensDigest(s: ShotSummaryInput): string | null {
  const parts = [
    lex(SHOT_SIZE_LABEL, s.shotSize),
    lex(CAMERA_MOVE_LABEL, s.cameraMovement),
    lex(LIGHTING_LABEL, s.lightingKey),
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}
